/**
 * Visual inheritance chain for session defaults (Workspace → Cycle → Track → Session).
 * Replaces long "— → — → …" strings with labeled layer cards and a clear "Used now" outcome.
 */

import { Fragment } from 'react';
import { ChevronRight } from 'lucide-react';
import type { SessionDefaultsScope } from '@/lib/effectiveConfigResolver';

const LAYER_TITLE: Record<'global' | 'cycle' | 'track' | 'session', string> = {
  global: 'Workspace',
  cycle: 'Cycle',
  track: 'Track',
  session: 'Session',
};

export type SessionDefaultLayerSnapshot = {
  global: string;
  /** The level above the cycle that set `global`, as the server read it (S-40). */
  globalLabel?: string;
  cycle: string;
  track: string;
  session: string;
  effective: string;
  source: SessionDefaultsScope;
};

function isUnsetCell(v: string): boolean {
  const t = (v ?? '').trim();
  return t === '' || t === '—' || t === '-' || t === '–';
}

function LayerCard({
  layerKey,
  value,
  isWinningLayer,
  title,
}: {
  layerKey: 'global' | 'cycle' | 'track' | 'session';
  value: string;
  isWinningLayer: boolean;
  title?: string;
}) {
  const unset = isUnsetCell(value);
  return (
    <div
      className={`flex min-w-0 flex-1 flex-col gap-0.5  border px-2.5 py-2 sm:max-w-[160px] ${
        isWinningLayer
          ? 'border-[var(--slate)] bg-[var(--slate-dim)]/90 dark:border-[var(--slate)]/70 dark:bg-[var(--slate)]/45 ring-1 ring-indigo-200/70 dark:ring-indigo-800/40'
          : 'border-[var(--rule)] bg-[var(--surface)]/90 dark:border-[var(--rule)] dark:bg-[var(--surface)]'
      }`}
    >
      <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-sec)]">
        {title ?? LAYER_TITLE[layerKey]}
      </span>
      <span
        className={`text-xs font-medium leading-snug ${
          unset ? 'italic text-[var(--text-ter)]' : 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'
        }`}
        title={unset ? undefined : value}
      >
        {unset ? 'Not set here' : value}
      </span>
    </div>
  );
}

function FlowChevron() {
  return (
    <span className="flex shrink-0 items-center self-center text-[var(--text-ter)]" aria-hidden>
      <ChevronRight className="h-4 w-4" />
    </span>
  );
}

export interface SessionDefaultsInheritanceChainRowsProps {
  /** One row per setting; `layer` is the per-key breakdown entry */
  rows: Array<{ key: string; label: string; layer: SessionDefaultLayerSnapshot }>;
  /** Cycle defaults UI: workspace + cycle only. Track defaults: workspace + cycle + track (+ session hint). */
  depth: 'cycle' | 'track';
}

export function SessionDefaultsInheritanceChainRows({ rows, depth }: SessionDefaultsInheritanceChainRowsProps) {
  const chainKeys =
    depth === 'cycle' ? (['global', 'cycle'] as const) : (['global', 'cycle', 'track'] as const);

  return (
    <div className="space-y-3">
      <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
        Values flow downward: each level can leave a setting unset so the level above still applies. The{' '}
        <span className="font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)]">Used now</span> box is what new
        sessions inherit at this point{depth === 'track' ? ' (sessions can still override when created)' : ''}.
      </p>
      {rows.map((row) => (
        <div
          key={row.key}
          className="border border-[var(--rule)] bg-[var(--surface)]/40 dark:border-[var(--rule)] dark:bg-[var(--surface)] p-3"
        >
          <div className="mb-2 text-xs font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{row.label}</div>
          <div className="flex flex-wrap items-stretch gap-y-2 sm:flex-nowrap sm:items-center">
            {chainKeys.map((lk, i) => (
              <Fragment key={lk}>
                {i > 0 && <FlowChevron />}
                <LayerCard
                  layerKey={lk}
                  value={row.layer[lk]}
                  isWinningLayer={row.layer.source === lk}
                  title={lk === 'global' ? row.layer.globalLabel : undefined}
                />
              </Fragment>
            ))}
            {depth === 'track' && (
              <>
                <FlowChevron />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5 border border-dashed border-[var(--rule)] px-2.5 py-2 sm:max-w-[160px]">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                    {LAYER_TITLE.session}
                  </span>
                  <span className="text-xs italic leading-snug text-[var(--text-ter)]">
                    Optional when you create or open a session
                  </span>
                </div>
              </>
            )}
            <FlowChevron />
            <div className="flex min-w-[108px] shrink-0 flex-col gap-0.5 border border-[var(--green)] bg-[var(--green-dim)]/90 px-2.5 py-2 dark:border-[var(--green)]/60 dark:bg-[var(--green)]/35">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--green)] dark:text-[var(--text-ter)]/90">
                Used now
              </span>
              <span
                className={`text-xs font-semibold leading-snug ${
                  isUnsetCell(row.layer.effective)
                    ? 'italic text-[var(--green)]/80 dark:text-[var(--text-ter)]/70'
                    : 'text-[var(--green)] dark:text-[var(--text-ter)]'
                }`}
              >
                {isUnsetCell(row.layer.effective) ? 'Not set' : row.layer.effective}
              </span>
              <span className="text-[10px] text-[var(--green)]/85 dark:text-[var(--text-ter)]/80">
                Source: {row.layer.source === 'global' && row.layer.globalLabel ? row.layer.globalLabel : LAYER_TITLE[row.layer.source]}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
